import { IonContent, IonGrid, IonPage } from '@ionic/react';
import { Suspense, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

import { OverviewReportTab } from '@/pages/ReportV2/OverviewReportTab';
// Styles
import styles from '@/pages/ReportV2/Styles/ReportDetailPage.module.css';
import { WalkAnalysisReportTab } from '@/pages/ReportV2/WalkReportV2/WalkAnalysisReportTab';
import { WalkJointCouplingReportTab } from '@/pages/ReportV2/WalkReportV2/WalkJointCouplingReportTab';
import { WalkKinematicsReportTab } from '@/pages/ReportV2/WalkReportV2/WalkKinematicsReportTab';
import { WalkMobilityReportTab } from '@/pages/ReportV2/WalkReportV2/WalkMobilityStatusReportTab';
import { WalkVariabilityReportTab } from '@/pages/ReportV2/WalkReportV2/WalkVariabilityReportTab';

import { FloatingButton } from '@/components/FloatingButton/FloatingButton';
// Utils
import { Loading } from '@/components/Loading/Loading';
// Components
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { PageTopBar } from '@/components/PageTopBar/PageTopBar';
import { SegmentedControl, type SegmentButton } from '@/components/SegmentedControl';
import { toast } from '@/components/Toaster/Toast';

import { getTitleAndDescriptionFromReportData } from '@/utils/getTitleAndDescriptionFromReportData';
import { mergeClassNames } from '@/utils/mergeClassNames';

// Hooks
import { createWalkReportData } from '@/hooks/useReports';

//Context & models
import { useReportContext } from '@/contexts/reportContext';

import { ReportInfoId } from '@/models';
import { WalkReportData } from '@/models/reportV2.model';

interface ParamTypes {
  id: ReportInfoId;
}
/** Report detail page
 * Loading the report data for the given userId and reportInfoId in the url.
 * Switch the tabs from Walk to Run depending on reportType in the loaded report data.
 * @param reportInfoId - the reportInfoId from the url
 */
export const WalkReportDetailPage = () => {
  const location = useLocation<{ title: string; description: string; backTarget?: string | null }>();
  const { id } = useParams<ParamTypes>();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('mobilitystatus');
  const [walkReportData, setWalkReportData] = useState<WalkReportData | undefined>();
  const [floatingButtons, setFloatingButtons] = useState<React.ReactNode>(null);

  const { getReportData, dataReady, data, setDataReady, resetData } = useReportContext();

  const { title, description, backTarget } = location.state || getTitleAndDescriptionFromReportData(walkReportData);
  const showOverviewTab = !backTarget;

  useEffect(() => {
    getReportData(id);
    // Cleanup the data when the component unmounts
    return () => {
      setLoading(true);
      setWalkReportData(undefined);
      setDataReady(false); // Reset dataReady in the context
      resetData();
    };
  }, [id]);

  useEffect(() => {
    if (data) {
      setWalkReportData(createWalkReportData(data));

      toast({
        icon: 'info',
        title: 'Enlarge graphs',
        description: 'Do this by clicking / tapping on the chart',
        duration: 3000,
      });
    } else {
      setWalkReportData(undefined);
    }
  }, [dataReady]);

  useEffect(() => {
    if (walkReportData !== undefined && data) setLoading(false);
  }, [walkReportData]);

  useEffect(() => {
    if (import.meta.env.MODE !== 'production') console.log('reportData', walkReportData);
  }, [walkReportData]);

  const handleOnSegmentClick = (target: string) => {
    setActiveTab(target);
    // history.push(`/reportsV2/Walk/${id}/${target}`);/
  };

  const segments: SegmentButton[] = [
    { title: 'Mobility status', value: 'mobilitystatus', onClick: () => handleOnSegmentClick('mobilitystatus') },
    { title: 'Analysis', value: 'analysis', onClick: () => handleOnSegmentClick('analysis') },
    { title: 'Kinematics', value: 'kinematics', onClick: () => handleOnSegmentClick('kinematics') },
    { title: 'Variability', value: 'variability', onClick: () => handleOnSegmentClick('variability') },
    { title: 'Joint coupling', value: 'jointcoupling', onClick: () => handleOnSegmentClick('jointcoupling') },
    ...(showOverviewTab
      ? [{ title: 'Overview', value: 'overview', onClick: () => handleOnSegmentClick('overview') } as SegmentButton]
      : []),
  ];

  return (
    <IonPage>
      <PageHeader
        isLoading={loading}
        topSlot={
          <PageTopBar
            backTarget={backTarget ? backTarget : '/reports'}
            description={description}
            title={title}
            variant='simple'
          />
        }>
        <SegmentedControl segments={segments} value={activeTab} variant='default' />
      </PageHeader>
      <div className={styles.background}>
        <IonContent className={mergeClassNames('ion-padding', styles.content)} color='light' id='main'>
          <IonGrid fixed style={{ height: '100%' }}>
            {loading && <Loading />}
            {walkReportData && (
              <>
                <Suspense fallback={<Loading />}>
                  {activeTab === 'mobilitystatus' && (
                    <WalkMobilityReportTab tabData={walkReportData.mobilityStatusData} />
                  )}
                  {activeTab === 'analysis' && <WalkAnalysisReportTab tabData={walkReportData.analysisData} />}
                  {activeTab === 'variability' && (
                    <WalkVariabilityReportTab
                      setFloatingButtons={setFloatingButtons}
                      tabData={walkReportData.walkVariabilityData}
                    />
                  )}
                  {activeTab === 'kinematics' && (
                    <WalkKinematicsReportTab
                      setFloatingButtons={setFloatingButtons}
                      tabData={walkReportData.walkKinematicData}
                    />
                  )}
                  {activeTab === 'jointcoupling' && (
                    <WalkJointCouplingReportTab tabData={walkReportData.walkJointCouplingData} />
                  )}
                  {showOverviewTab && activeTab === 'overview' && (
                    <OverviewReportTab
                      fileInfo={walkReportData.fileInfo}
                      id={id}
                      reportType={walkReportData.reportType}
                    />
                  )}
                </Suspense>
              </>
            )}
          </IonGrid>
        </IonContent>
        {floatingButtons && (
          <FloatingButton horizontal='end' side='top' slot='fixed' vertical='bottom'>
            {floatingButtons}
          </FloatingButton>
        )}
      </div>
    </IonPage>
  );
};
